import { Grid, Box, Stack, Typography } from '@mui/material';

import Form from 'components/Form';
import { DateBoundTimePicker } from 'components/TimePicker/DateBoundTimePicker';
import { coercePickerDate } from 'utils/timePickerBridge';
import { IFormRegister, IFormValues, IFormWatch } from 'routes/Main/type';
import RowContainer from '../components/RowContainer';
import RowContent from '../components/RowContent';

import SectionTitle from '../components/SectionTitle';

interface Props extends IFormRegister, IFormValues, IFormWatch {
  disabled?: boolean;
}

// 'YYYY-MM-DD' → {y,m,d}
const parseYMD = (dateStr?: string | null) => {
  if (!dateStr) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
  if (!m) return null;
  return { y: +m[1], mo: +m[2], d: +m[3] };
};

// Date → 'YYYY-MM-DDTHH:mm:ss+09:00' (로컬 오프셋 ISO)
const toOffsetIsoLocal = (d: Date) => {
  const tzMin = -d.getTimezoneOffset();
  const sign = tzMin >= 0 ? '+' : '-';
  const oh = String(Math.trunc(Math.abs(tzMin) / 60)).padStart(2, '0');
  const om = String(Math.abs(tzMin) % 60).padStart(2, '0');
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const HH = String(d.getHours()).padStart(2, '0');
  const MM = String(d.getMinutes()).padStart(2, '0');
  const SS = String(d.getSeconds()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}T${HH}:${MM}:${SS}${sign}${oh}:${om}`;
};

const checks_matters = [
  {
    label: '주사침 자상',
    key: 'infection_matters.checked1',
  },
  {
    label: '감염 노출',
    key: 'infection_matters.checked2',
  },
];

const checks_left = [
  {
    label: '1',
    key: 'infection_left.checked1',
  },
  {
    label: '2',
    key: 'infection_left.checked2',
  },
  {
    label: '3',
    key: 'infection_left.checked3',
  },
  {
    label: '4',
    key: 'infection_left.checked4',
  },
  {
    label: '5',
    key: 'infection_left.checked5',
  },
];

const checks_right = [
  {
    label: '1',
    key: 'infection_right.checked1',
  },
  {
    label: '2',
    key: 'infection_right.checked2',
  },
  {
    label: '3',
    key: 'infection_right.checked3',
  },
  {
    label: '4',
    key: 'infection_right.checked4',
  },
  {
    label: '5',
    key: 'infection_right.checked5',
  },
];

const checks_situation = [
  {
    label: '주사를 놓거나 채혈하다가 (환자가 비협조적이거나 불안정하여)',
    key: 'infection_situation.checked1',
  },
  {
    label: '주사를 놓거나 채혈하다가',
    key: 'infection_situation.checked2',
  },
  {
    label: '혈당검사 관련 업무 시행 중',
    key: 'infection_situation.checked3',
  },
  {
    label: '주사바늘 사용 후 Recapping 하다가',
    key: 'infection_situation.checked4',
  },
  {
    label: '시술 후 물품 정리하다가',
    key: 'infection_situation.checked5',
  },
  {
    label: '린넨이나 쓰레기를 치우다가',
    key: 'infection_situation.checked6',
  },
  {
    label: 'ABGA (고무마개를 막다가, 바늘을 빼다가)  -  기타  ',
    key: 'infection_situation.checked7',
  },
  {
    label: '수술 도중이나 시술 후 기구나 바늘에 찔린 경우',
    key: 'infection_situation.checked8',
  },
  {
    label: '기타',
    key: 'infection_situation.checked9',
  },
];

const checks_cause = [
  {
    label: '주사바늘',
    key: 'infection_cause.checked1',
  },
  {
    label: 'Scalp needle',
    key: 'infection_cause.checked2',
  },
  {
    label: 'Angio cath',
    key: 'infection_cause.checked3',
  },
  {
    label: 'Lanset',
    key: 'infection_cause.checked4',
  },
  {
    label: '칼',
    key: 'infection_cause.checked5',
  },
  {
    label: '기구',
    key: 'infection_cause.checked6',
  },
  {
    label: '기타',
    key: 'infection_cause.checked7',
  },
];

const checked_gloves = [
  {
    label: '',
    key: 'wear_gloves_none.checked1',
  },
];

const Exposure = (props: Props) => {
  const { disabled, register, getValues, setValue, watch } = props;

  const rawTime = watch('infection_time');
  const dateField = watch('infection_date');

  const valueForPicker: Date | null = (() => {
    if (!rawTime) return null;

    const timeDate = coercePickerDate(rawTime);
    if (!timeDate) return null;

    const ymd = parseYMD(dateField);
    const result = new Date();

    if (ymd) {
      result.setFullYear(ymd.y, ymd.mo - 1, ymd.d);
    }

    result.setHours(timeDate.getHours(), timeDate.getMinutes(), 0, 0);
    return result;
  })();

  return (
    <>
      <SectionTitle title="2. 노출 사항" mt={3} mb={3} />
      <RowContainer xs={12} sx={{ marginBottom: '20px' }}>
        <RowContent title="노출 사항" titleRatio={1} childrenRatio={11}>
          <Stack direction="row">
            {checks_matters.map((v, i) => {
              return (
                <Grid item xs={i === 6 ? 0.6 : 1.4}>
                  <Stack direction="row">
                    <Form.MuiCheckbox
                      label={v.label}
                      disabled={disabled}
                      defaultValue={Boolean(getValues(v.key)) ? [v.label] : []}
                      onChange={(_, checked) => {
                        setValue(v.key, checked);
                      }}
                    />
                  </Stack>
                </Grid>
              );
            })}
          </Stack>
        </RowContent>
        <RowContent title="발생 일시" titleRatio={1} childrenRatio={11}>
          <Stack direction="row" spacing={3}>
            <Grid item xs={2.8}>
              <Stack direction="row" spacing={3}>
                <Form.MuiTextField
                  type="date"
                  required={false}
                  disabled={disabled}
                  {...register(`infection_date`)}
                />
                <DateBoundTimePicker
                  disabled={disabled}
                  value={valueForPicker}
                  onChange={val => {
                    const timeDate =
                      val instanceof Date && !isNaN(val.getTime()) ? val : null;

                    if (!timeDate) {
                      setValue('infection_time', '');
                      return;
                    }

                    const ymd = parseYMD(getValues('infection_date'));
                    const finalDate = new Date();
                    if (ymd) {
                      finalDate.setFullYear(ymd.y, ymd.mo - 1, ymd.d);
                    } else {
                      finalDate.setFullYear(
                        timeDate.getFullYear(),
                        timeDate.getMonth(),
                        timeDate.getDate()
                      );
                    }
                    finalDate.setHours(
                      timeDate.getHours(),
                      timeDate.getMinutes(),
                      0,
                      0
                    );

                    setValue('infection_time', toOffsetIsoLocal(finalDate));
                  }}
                  placeholder="00:00"
                  usePortal={false}
                />
              </Stack>
            </Grid>
          </Stack>
        </RowContent>
        <RowContent title="발생장소" titleRatio={1} childrenRatio={11}>
          <Stack direction="row" spacing={3}>
            <Grid item xs={2.8}>
              <Stack direction="row" spacing={3}>
                <Form.MuiTextField
                  required={false}
                  disabled={disabled}
                  {...register(`infection_place`)}
                />
              </Stack>
            </Grid>
          </Stack>
        </RowContent>
        <RowContent title="노출부위" titleRatio={1} childrenRatio={11}>
          <Stack direction="row" spacing={3}>
            <Grid item xs={0.4}>
              <Typography
                variant="caption"
                fontWeight={400}
                lineHeight="38px"
                sx={{ whiteSpace: 'nowrap' }}
              >
                왼손
              </Typography>
            </Grid>
            {checks_left.map((v, i) => {
              return (
                <Grid item xs={1.4}>
                  <Stack direction="row">
                    <Form.MuiCheckbox
                      label={v.label}
                      disabled={disabled}
                      defaultValue={Boolean(getValues(v.key)) ? [v.label] : []}
                      onChange={(_, checked) => {
                        setValue(v.key, checked);
                      }}
                    />
                  </Stack>
                </Grid>
              );
            })}
          </Stack>
        </RowContent>
        <RowContent title="" titleRatio={1} childrenRatio={11}>
          <Stack direction="row" spacing={3}>
            <Grid item xs={0.4}>
              <Typography
                variant="caption"
                fontWeight={400}
                lineHeight="38px"
                sx={{ whiteSpace: 'nowrap' }}
              >
                오른손
              </Typography>
            </Grid>
            {checks_right.map((v, i) => {
              return (
                <Grid item xs={1.4}>
                  <Stack direction="row">
                    <Form.MuiCheckbox
                      label={v.label}
                      disabled={disabled}
                      defaultValue={Boolean(getValues(v.key)) ? [v.label] : []}
                      onChange={(_, checked) => {
                        setValue(v.key, checked);
                      }}
                    />
                  </Stack>
                </Grid>
              );
            })}
          </Stack>
        </RowContent>
        <RowContent title="" titleRatio={1} childrenRatio={11}>
          <Stack direction="row" spacing={3}>
            <Grid item xs={0.4}>
              <Typography
                variant="caption"
                fontWeight={400}
                lineHeight="38px"
                sx={{ whiteSpace: 'nowrap' }}
              >
                기타
              </Typography>
            </Grid>
            <Grid item xs={1.6}>
              <Form.MuiTextField
                required={false}
                disabled={disabled}
                {...register(`assessment_iv_input`)}
              />
            </Grid>
          </Stack>
        </RowContent>
        <RowContent title="노출 시 업무" titleRatio={1} childrenRatio={11}>
          {checks_situation.map((v, i) => {
            if (i === 6) {
              return (
                <Grid item xs={12}>
                  <Stack direction="row">
                    <Form.MuiCheckbox
                      label={v.label}
                      disabled={disabled}
                      defaultValue={Boolean(getValues(v.key)) ? [v.label] : []}
                      onChange={(_, checked) => {
                        setValue(v.key, checked);
                      }}
                    />
                    <Form.MuiTextField
                      required={false}
                      sx={{ width: '250px', marginLeft: '10px' }}
                      disabled={disabled}
                      placeholder="직접 입력"
                      {...register(`infection_cause_input`)}
                    />
                  </Stack>
                </Grid>
              );
            }
            if (i === 8) {
              return (
                <Grid item xs={12}>
                  <Stack direction="row">
                    <Form.MuiCheckbox
                      label={v.label}
                      disabled={disabled}
                      defaultValue={Boolean(getValues(v.key)) ? [v.label] : []}
                      onChange={(_, checked) => {
                        setValue(v.key, checked);
                      }}
                    />
                    <Form.MuiTextField
                      required={false}
                      sx={{ width: '250px', marginLeft: '10px' }}
                      disabled={disabled}
                      placeholder="직접 입력"
                      {...register(`infection_cause_input`)}
                    />
                  </Stack>
                </Grid>
              );
            }

            return (
              <Grid item xs={12}>
                <Form.MuiCheckbox
                  label={v.label}
                  disabled={disabled}
                  defaultValue={Boolean(getValues(v.key)) ? [v.label] : []}
                  onChange={(_, checked) => {
                    setValue(v.key, checked);
                  }}
                />
              </Grid>
            );
          })}
        </RowContent>

        <RowContent title="노출 원인" titleRatio={1} childrenRatio={11}>
          <Stack direction="row">
            {checks_cause.map((v, i) => {
              return (
                <Grid item xs={i === 6 ? 0.6 : 1.4}>
                  <Stack direction="row">
                    <Form.MuiCheckbox
                      label={v.label}
                      disabled={disabled}
                      defaultValue={Boolean(getValues(v.key)) ? [v.label] : []}
                      onChange={(_, checked) => {
                        setValue(v.key, checked);
                      }}
                    />
                  </Stack>
                </Grid>
              );
            })}
            <Form.MuiTextField
              required={false}
              sx={{ width: '120px' }}
              disabled={disabled}
              placeholder="직접 입력"
              {...register(`infection_cause_input`)}
            />
          </Stack>
        </RowContent>
        <RowContent title="장갑 착용" titleRatio={1} childrenRatio={11}>
          <Stack direction="row">
            <Grid item xs={0.7}>
              <Typography
                variant="caption"
                fontWeight={400}
                lineHeight="38px"
                sx={{ whiteSpace: 'nowrap' }}
              >
                멸균장갑
              </Typography>
            </Grid>
            <Form.MuiRadioGroup
              i18nKey="EXIST.SHORT"
              values={[2, 1]}
              disabled={disabled}
              defaultValue={getValues('wear_gloves.value1')}
              onChange={v => setValue('wear_gloves.value1', v)}
            />
          </Stack>
        </RowContent>
        <RowContent title="" titleRatio={1} childrenRatio={11}>
          <Stack direction="row">
            <Grid item xs={0.7}>
              <Typography
                variant="caption"
                fontWeight={400}
                lineHeight="38px"
                sx={{ whiteSpace: 'nowrap' }}
              >
                비닐장갑
              </Typography>
            </Grid>
            <Form.MuiRadioGroup
              i18nKey="EXIST.SHORT"
              values={[2, 1]}
              disabled={disabled}
              defaultValue={getValues('wear_gloves.value2')}
              onChange={v => setValue('wear_gloves.value2', v)}
            />
          </Stack>
        </RowContent>
        <RowContent title="" titleRatio={1} childrenRatio={11}>
          <Stack direction="row">
            <Grid item xs={0.82}>
              <Typography
                variant="caption"
                fontWeight={400}
                lineHeight="38px"
                sx={{ whiteSpace: 'nowrap' }}
              >
                미착용
              </Typography>
            </Grid>
            {checked_gloves.map((v, i) => {
              return (
                <Grid item xs={1.25}>
                  <Stack direction="row">
                    <Form.MuiCheckbox
                      label={v.label}
                      disabled={disabled}
                      defaultValue={Boolean(getValues(v.key)) ? [v.label] : []}
                      onChange={(_, checked) => {
                        setValue(v.key, checked);
                      }}
                    />
                  </Stack>
                </Grid>
              );
            })}
          </Stack>
        </RowContent>
        <RowContent title="목격자" titleRatio={1} childrenRatio={11}>
          <Stack direction="row" spacing={3}>
            <Grid item xs={1.2}>
              <Typography
                variant="caption"
                fontWeight={400}
                lineHeight="38px"
                sx={{ whiteSpace: 'nowrap' }}
              >
                위 내용과 다름이 없음
              </Typography>
            </Grid>
            <Form.MuiTextField
              required={false}
              sx={{ width: '190px' }}
              disabled={disabled}
              placeholder="성명"
              {...register(`assessment_iv_input`)}
            />
            <Form.MuiTextField
              required={false}
              sx={{ width: '190px' }}
              disabled={disabled}
              placeholder="서명"
              {...register(`assessment_iv_date`)}
            />
          </Stack>
        </RowContent>
        <RowContent title="부서 책임자" titleRatio={1} childrenRatio={11}>
          <Stack direction="row" spacing={3}>
            <Grid item xs={1.2}>
              <Typography
                variant="caption"
                fontWeight={400}
                lineHeight="38px"
                sx={{ whiteSpace: 'nowrap' }}
              >
                위 내용과 다름이 없음
              </Typography>
            </Grid>
            <Form.MuiTextField
              required={false}
              sx={{ width: '190px' }}
              disabled={disabled}
              placeholder="성명"
              {...register(`assessment_iv_input`)}
            />
            <Form.MuiTextField
              required={false}
              sx={{ width: '190px' }}
              disabled={disabled}
              placeholder="서명"
              {...register(`assessment_iv_date`)}
            />
          </Stack>
        </RowContent>
      </RowContainer>
    </>
  );
};

export default Exposure;
